/**
 * Portfolio triage for the Stores hub: turns each store's 30d vs prior-30d summary into a short list of
 * plain-language issues ("Spending with no sales", "Sales down 28% while spend is up") so an agency can
 * see which stores need action today without scanning 80+ rows.
 */

export const ATTENTION_SEVERITY = {
  critical: { key: 'critical', label: 'Act now', rank: 3 },
  warning: { key: 'warning', label: 'Watch', rank: 2 },
  opportunity: { key: 'opportunity', label: 'Scale up', rank: 1 },
};

const MIN_SPEND_SIGNAL = 500;
const MIN_SPEND_LOSS = 1000;

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function pct(v) {
  return `${Math.abs(Math.round(v))}%`;
}

function money(v) {
  const x = Math.round(n(v));
  if (x >= 10000000) return `₹${(x / 10000000).toFixed(1)}Cr`;
  if (x >= 100000) return `₹${(x / 100000).toFixed(1)}L`;
  if (x >= 1000) return `₹${(x / 1000).toFixed(1)}k`;
  return `₹${x}`;
}

/** @returns {Array<{key:string, severity:'critical'|'warning'|'opportunity', title:string, detail:string, atRiskSpend?:number}>} */
export function evaluateStoreAttention(summary) {
  const cur = summary?.current;
  if (!cur) return [];

  const spend = n(cur.spend);
  const sales = n(cur.sales);
  const acos = sales > 0 ? (spend / sales) * 100 : null;
  const roas = spend > 0 ? sales / spend : null;
  const dSales = summary?.deltaPct?.sales;
  const dSpend = summary?.deltaPct?.spend;
  const prevSales = n(summary?.previous?.sales);
  const issues = [];

  if (spend >= MIN_SPEND_SIGNAL && sales <= 0) {
    issues.push({
      key: 'no_sales',
      severity: 'critical',
      title: 'Spending with no sales',
      detail: `${money(spend)} spent, no sales in 30d`,
      atRiskSpend: spend,
    });
  } else if (spend >= MIN_SPEND_LOSS && roas != null && roas < 1) {
    issues.push({
      key: 'losing_money',
      severity: 'critical',
      title: 'Ads cost more than they earn',
      detail: `ROAS ${roas.toFixed(2)} · ACOS ${Math.round(acos)}% on ${money(spend)}`,
      atRiskSpend: spend,
    });
  } else if (spend >= MIN_SPEND_SIGNAL && acos != null && acos > 45) {
    issues.push({
      key: 'high_acos',
      severity: 'warning',
      title: 'High ACOS',
      detail: `ACOS ${Math.round(acos)}% on ${money(spend)} spend`,
    });
  }

  const salesDropped = dSales != null && dSales <= -20 && prevSales >= 2000;
  if (salesDropped && (dSpend == null || dSpend >= -5)) {
    issues.push({
      key: 'sales_drop',
      severity: 'critical',
      title: 'Sales falling',
      detail: dSpend != null && dSpend > 0
        ? `Sales −${pct(dSales)} while spend +${pct(dSpend)}`
        : `Sales −${pct(dSales)} vs prior 30d`,
    });
  } else if (dSales != null && dSales <= -10 && prevSales >= 2000) {
    issues.push({
      key: 'sales_dip',
      severity: 'warning',
      title: 'Sales slipping',
      detail: `Sales −${pct(dSales)} vs prior 30d`,
    });
  }

  if (
    !salesDropped
    && dSpend != null && dSpend >= 30
    && (dSales == null || dSales < dSpend - 25)
    && spend >= MIN_SPEND_SIGNAL
  ) {
    issues.push({
      key: 'spend_outpacing',
      severity: 'warning',
      title: 'Spend growing faster than sales',
      detail: dSales != null
        ? `Spend +${pct(dSpend)} · sales ${dSales >= 0 ? '+' : '−'}${pct(dSales)}`
        : `Spend +${pct(dSpend)}`,
    });
  }

  if (
    !issues.length
    && dSales != null && dSales >= 20
    && acos != null && acos <= 35
    && spend >= MIN_SPEND_LOSS
  ) {
    issues.push({
      key: 'scale',
      severity: 'opportunity',
      title: 'Room to scale',
      detail: `Sales +${pct(dSales)} at ${Math.round(acos)}% ACOS — raise budgets`,
    });
  }

  return issues;
}

function severityOf(issues) {
  let best = null;
  issues.forEach((i) => {
    if (!best || ATTENTION_SEVERITY[i.severity].rank > ATTENTION_SEVERITY[best].rank) best = i.severity;
  });
  return best;
}

/**
 * @param {Array} groups   store groups (as on the hub list)
 * @param {Object} summaries profileId → performance summary
 */
export function buildAttentionIndex(groups, summaries) {
  const byGroup = new Map();
  const counts = { critical: 0, warning: 0, opportunity: 0 };
  let atRiskSpend = 0;
  let atRiskStores = 0;

  (groups || []).forEach((group) => {
    const store = group.primaryStore;
    if (!store?.isConnected) return;
    const pid = String(store.profileId || store.storeId || '');
    const summary = summaries?.[pid];
    const issues = evaluateStoreAttention(summary);
    if (!issues.length) return;
    const severity = severityOf(issues);
    counts[severity] += 1;
    const risk = issues.reduce((s, i) => s + n(i.atRiskSpend), 0);
    if (risk > 0) {
      atRiskSpend += risk;
      atRiskStores += 1;
    }
    issues.sort((a, b) => ATTENTION_SEVERITY[b.severity].rank - ATTENTION_SEVERITY[a.severity].rank);
    byGroup.set(group.id, {
      group,
      store,
      issues,
      severity,
      spend: n(summary?.current?.spend),
      // Bigger spenders first within a severity: that is where a fix moves the most money.
      score: ATTENTION_SEVERITY[severity].rank * 1e9 + n(summary?.current?.spend),
    });
  });

  return { byGroup, counts, atRiskSpend, atRiskStores };
}

export { money as formatAttentionMoney };
